import { required } from './objectUtils'

// We are using .env instead of a .json, because CI can manage these values via web UI, instead of managing files before the build/publish.
// Environment variables are read from:
// 1) from .env files in mpsklad-core/ClientApp,
// 2) the actual env, which is set by CI at build time,
// except NODE_ENV, which is set by react-scripts
// NOTE: JS tests only read .env and .env.test, but not .local (for consistent test results via react-scripts)

const badEnvValue =
  (name: string) => {
    throw new Error(`Environment variable is invalid for ${name}!`)
  }

const nodeEnv: string =
  required(process.env['NODE_ENV']) || badEnvValue('node env')

/*
* `true` for Production and Staging.
* `false` for local development and any JS tests (when launching via `npm start` or `npm test`).
* */
const isProduction: boolean =
  nodeEnv === 'production'

const getApiBaseUrl =
  (): string => {
    const url = required(process.env['REACT_APP_API_URL']) || badEnvValue('API URL')

    if (!url.endsWith('/')) {
      throw new Error(`Bad environment variable for API URL - must end with a slash: ${url}`)
    }

    if (isProduction && !url.startsWith('https://')) {
      throw new Error(`Bad environment variable for API URL - must be HTTPS: ${url}`)
    }

    return url
  }

const getApiTimeoutSeconds =
  (): number => {
    const timeout = parseInt(required(process.env['REACT_APP_API_TIMEOUT']) || badEnvValue('API timeout'))

    if (isNaN(timeout) || timeout <= 0) {
      throw Error(`Bad environment variable for API timeout: ${timeout}`)
    }

    return timeout
  }

const getGrafanaErrorUrl =
  (): string => {
    const url = required(process.env['REACT_APP_GRAFANA_ERROR_URL']) || badEnvValue('Grafana error URL')

    if (isProduction && !url.startsWith('https://')) {
      throw new Error(`Bad environment variable for Grafana error URL - must be HTTPS: ${url}`)
    }

    return url
  }

export const appConfig: {
  readonly isProduction: boolean

  /** Ends with a slash. */
  readonly apiBaseUrl: string

  readonly apiTimeoutSeconds: number

  readonly grafanaErrorUrl: string
} = {
  isProduction,
  apiBaseUrl: getApiBaseUrl(),
  apiTimeoutSeconds: getApiTimeoutSeconds(),
  grafanaErrorUrl: getGrafanaErrorUrl()
} as const

export const suppressMuiReactWarnings = () => {
  let suppressedWarningCount = 0
  const originalError = console.error
  const strictModeFindDomNodeComponents = ['Transition', 'Tooltip']

  console.error =
    function (...args: unknown[]) {
      if ((args.length >= 4
           && typeof args[0] === 'string'
           && args[0].startsWith('Warning: %s is deprecated in StrictMode. %s was passed an instance of %s')
           && args[1] === 'findDOMNode'
           && typeof args[3] === 'string'
           && strictModeFindDomNodeComponents.includes(args[3]))
          || (args.length === 1
              && typeof args[0] === 'string'
              && args[0].startsWith('Material-UI: The `fade` color utility was renamed to `alpha`'))) {
        suppressedWarningCount++
        console.log(`Suppressed MaterialUI React warnings: ${suppressedWarningCount}`)
        return
      }

      originalError.call(this, ...args)
    }
}